import * as React from "react";
import { observer } from "mobx-react";

import {
  UitkLayoutGrid,
  UitkLayoutConditionalGridSpan,
  UitkLayoutConditionalGridColumnRow,
} from "uitk-react-layout-grid";
import { UitkSpacing } from "uitk-react-spacing";

import { Viewport, ViewSmall, ViewMedium } from "@shared-ui/viewport-context";

import { SubmitRow } from "components/shared/StorefrontWizard/SubmitRow/SubmitRow";
import { LobErrorSummary } from "components/shared/LobErrorSummary/LobErrorSummary";
import { Location } from "components/shared/Location/Location";
import { Dates } from "components/shared/Dates/Dates";
import { TimeDropdown } from "components/shared/TimeDropdown/TimeDropdown";

import { VendorCodes } from "../vendorOptions/VendorCodes";
import { CarsHiddenSearchData } from "../CarsHiddenSearchData";
import { CarTrawlerHiddenSearchData } from "../CarTrawlerHiddenSearchData";
import { CarsFormDefaultViewProps } from "../../typings";
import { locKeys } from "../l10n";
import { DifferentDropOffToggle } from "../DifferentDropOffToggle/DifferentDropOffToggle";
import { DriverAgeInRangeToggle } from "../DriverAgeInRangeToggle";
import DriverAgeInputField from "../DriversAgeInputField";
import { AARPCheckbox } from "../AARPCheckbox";
import { CarTypeDropdown } from "../vendorOptions/CarTypeDropdown";
import { ViewLarge } from "bernie-view";

export const CarsFormView: React.FunctionComponent<CarsFormDefaultViewProps> = observer((props) => {
  const {
    carWizardState,
    carConfig,
    isDiffDropOff,
    onDifferentDropOffSelected,
    setIsDifferentDropOff,
    isDriverAgeInRange,
    isAARPChecked,
    onDriverAgeInRange,
    onAARPChange,
    setInputsRefs,
    globalConfig,
  } = props;
  const halfWizard = globalConfig?.halfWizard;
  const { pickUpTimeLabel, dropOffTimeLabel } = locKeys;
  const format12hrConfig = carWizardState.config.timeFormats.timeFormat12Hour;
  const { isCarFormValid, updateInvalidFormsState } = carWizardState;
  const mainGridColumns: UitkLayoutConditionalGridColumnRow = { small: 2, medium: 12 };
  const locationColSpan: UitkLayoutConditionalGridSpan = { small: 2, medium: 6 };
  const datesColSpan: UitkLayoutConditionalGridSpan = { small: 2, medium: 12, large: halfWizard ? 12 : 6 };
  const timeDropdownColSpan: UitkLayoutConditionalGridSpan = { small: 1, medium: 6, large: halfWizard ? 6 : 3 };
  const couponColSpan: UitkLayoutConditionalGridSpan = { small: 2, medium: halfWizard ? 6 : 4 };
  const driversAgeColSpan: UitkLayoutConditionalGridSpan = { small: 2, medium: 6 };

  if (isCarFormValid) {
    updateInvalidFormsState();
  }

  React.useEffect(() => {
    const VIEWPORT_MAX_SMALL_BREAKPOINT = 575;
    const { innerWidth: viewportSize } = window;

    if (viewportSize > VIEWPORT_MAX_SMALL_BREAKPOINT) {
      setIsDifferentDropOff(true);
    } else {
      setIsDifferentDropOff(false);
    }
  }, []);

  return (
    <>
      <Viewport>
        <ViewSmall>
          <>
            {!carConfig.isBiasedWizardEmpty && !carConfig.carType && (
              <CarTypeDropdown carWizardState={carWizardState} />
            )}
          </>
        </ViewSmall>
        <ViewLarge />
        <ViewMedium />
      </Viewport>
      <UitkSpacing padding={{ blockstart: "three" }}>
        <UitkLayoutGrid columns={mainGridColumns} space="three">
          {!isCarFormValid && (
            <LobErrorSummary
              locHeadingToken={props.getLocError(false)!}
              locHeadingArgs={carWizardState.moreThanOneError() && [carWizardState.numberOfErrors]}
              locLinkToken={props.getLocError(true)}
              linkClickFocusId="id-roundtrip-error"
              inputErrorRef={carWizardState.errorInputRef}
              colSpan={mainGridColumns}
              setInputsRefs={setInputsRefs}
            />
          )}

          <Location
            setInputsRefs={setInputsRefs}
            originField={true}
            destinationField={false}
            lobState={carWizardState}
            colSpan={locationColSpan}
            globalConfig={globalConfig}
          />
          <Viewport>
            <ViewMedium>
              <Location
                originField={false}
                destinationField={true}
                lobState={carWizardState}
                colSpan={locationColSpan}
                globalConfig={globalConfig}
              />
            </ViewMedium>
          </Viewport>
        </UitkLayoutGrid>
      </UitkSpacing>

      <Viewport>
        <ViewSmall>
          <>
            <UitkSpacing margin={{ blockstart: "three" }}>
              <UitkLayoutGrid columns={mainGridColumns} space="three">
                <DifferentDropOffToggle
                  isDiffDropOff={isDiffDropOff}
                  onDifferentDropOffSelected={onDifferentDropOffSelected}
                  labelToken={carConfig.location.origin.differentDropOffToken}
                />
              </UitkLayoutGrid>
            </UitkSpacing>

            <UitkSpacing margin={{ blockstart: "three" }}>
              <UitkLayoutGrid columns={mainGridColumns} space="three">
                {isDiffDropOff && (
                  <Location
                    originField={false}
                    destinationField={true}
                    lobState={carWizardState}
                    colSpan={locationColSpan}
                    globalConfig={globalConfig}
                  />
                )}
              </UitkLayoutGrid>
            </UitkSpacing>
          </>
        </ViewSmall>
        <ViewMedium />
      </Viewport>

      <UitkSpacing margin={{ blockstart: "three" }}>
        <UitkLayoutGrid columns={mainGridColumns} space="three">
          <Dates
            lobState={carWizardState}
            colSpan={datesColSpan}
            singleDateSelect={true}
            setInputsRefs={setInputsRefs}
          />
          <TimeDropdown
            labelKey={pickUpTimeLabel}
            timeValues={carWizardState.config.timeValues}
            onChange={carWizardState.setPickupTime}
            value={carWizardState.pickupTime}
            invalidKey={carWizardState.timeStartInvalidKey}
            colSpan={timeDropdownColSpan}
            use12hr={format12hrConfig}
            showTimeIcon={true}
          />
          <TimeDropdown
            labelKey={dropOffTimeLabel}
            timeValues={carWizardState.config.timeValues}
            onChange={carWizardState.setReturnTime}
            value={carWizardState.returnTime}
            colSpan={timeDropdownColSpan}
            use12hr={format12hrConfig}
            showTimeIcon={true}
          />

          {carConfig.driveAgeCheckbox && (
            <DriverAgeInRangeToggle
              isDriverAgeInRange={isDriverAgeInRange}
              onDriverAgeInRange={onDriverAgeInRange}
              carConfig={carConfig}
            />
          )}
          {carConfig.driveAgeCheckbox && !isDriverAgeInRange && (
            <DriverAgeInputField
              carWizardState={carWizardState}
              colSpan={driversAgeColSpan}
              setInputsRefs={setInputsRefs!}
            />
          )}
          {carConfig.AARPCheckbox && <AARPCheckbox isAARPChecked={isAARPChecked} onAARPChange={onAARPChange} />}
        </UitkLayoutGrid>
      </UitkSpacing>

      {carConfig.showVendorOptions && (
        <VendorCodes
          carWizardState={carWizardState}
          mainColSpan={mainGridColumns}
          colSpan={couponColSpan}
          globalConfig={globalConfig}
        />
      )}

      <SubmitRow lobState={carWizardState} rfrr={"SearchClick_Cars"} globalConfig={globalConfig} />

      {!carConfig.isCarTrawler ? (
        <CarsHiddenSearchData lobState={carWizardState} />
      ) : (
        <CarTrawlerHiddenSearchData lobState={carWizardState} />
      )}
    </>
  );
});
